import { createSelector } from '@reduxjs/toolkit';
import { appInsight, collectProperties } from 'components/appInsight';
import { BannerLandscape, BannerLandscapeWrapper } from 'components/banner/bannerLandscape';
import { QuickSlot } from 'components/quickSlot';
import { AppFragment } from 'enums/path-fragment';
import { ApiStatus } from 'features/api/thunkUtils';
import { useIsLivePage, useScommessaStatus } from 'features/sport/hooks';
import { selectSlug } from 'features/sport/selectors';
import { RootState, useTypedSelector } from 'lib/centralStore';
import { DatoCmsVisualComposer } from 'lib/datoCms/types';
import { convertInBannerType } from 'lib/datoCms/utils';
import { enManagedComponents, managedComponentsOptions, ManagedComponentsType } from 'lib/ssr';
import React, { useEffect, useMemo } from 'react';
import { isMatch, isTruthy } from 'utility/functions';
import { SportAntepost } from '../sportAntepost';
import { SportInEvidenza } from '../sportInEvidenza';
import { SportPiuGiocate } from '../sportPiuGiocate';
import { SportTopManifestazioni } from '../sportTopManifestazioni';

type PossibleComponent =
  | ManagedComponentsType
  | 'squadre_block'
  | 'top_vincite_block'
  | 'snai_runner_block'
  | 'protagonisti_block'
  | 'guarda_su_snai_block'
  | 'prossimi_eventi_block'
  | 'corse_in_partenza_block';

export type PossibleTemplate = 'templateLive' | 'templateManifestazione' | 'templateDisciplina' | 'templateAvvenimento';

const selectSport = (state: RootState) => state?.sport;

export const selectTemplate = (possibleTemplate: PossibleTemplate) =>
  createSelector([selectSport, (state) => possibleTemplate], (sport, possibleTemplate) => {
    const template = Reflect.get(sport, possibleTemplate);
    const templateStatus = Reflect.get(sport, `${possibleTemplate}Status`);

    return {
      template,
      isError: templateStatus === ApiStatus.failed,
      isLoading: templateStatus === ApiStatus.loading,
    };
  });

interface SportComposerProps {
  pageId: string;
  cmsData: DatoCmsVisualComposer[];
  templateSlug: PossibleTemplate;
}

const SportComposer = ({ pageId, cmsData, templateSlug }: SportComposerProps) => {
  const { isError: isScommessaError } = useScommessaStatus();
  const { isError: isTemplateError } = useTypedSelector(selectTemplate(templateSlug));

  const slug = useTypedSelector(selectSlug);
  const isLive = useIsLivePage();

  const isTopTemplate = !isLive && isMatch(slug, `^${AppFragment.Sport}$`);

  useEffect(() => {
    if (isTemplateError || isScommessaError || !isTruthy(cmsData?.length)) {
      appInsight?.trackException({
        error: new Error('Unable to draw table'),
        properties: collectProperties(true, { pageId, isTemplateError, isScommessaError }),
      });
    }
  }, [cmsData?.length, isScommessaError, isTemplateError, pageId]);

  const components = useMemo(() => {
    let bannerCounter = -1;
    return (cmsData ?? [])
      .filter((x) => managedComponentsOptions.includes(x._modelApiKey as enManagedComponents))
      .map((data, index) => {
        const { quickSlot, title: titleData, _modelApiKey, banners } = data ?? {};
        const title = (quickSlot ? quickSlot.title : titleData) ?? '';
        const subtitle = (quickSlot ? quickSlot.subtitle : '') ?? '';
        const key = `${_modelApiKey}${index}`;

        switch (_modelApiKey as PossibleComponent) {
          case enManagedComponents.promo: {
            bannerCounter += 1;
            return (
              <BannerLandscapeWrapper
                key={key}
                pageId={pageId}
                promoNumber={bannerCounter}
                removePaddingLeft
                removePaddingRight={isTopTemplate}
              />
            );
          }
          case enManagedComponents.quick_slot: {
            return quickSlot && <QuickSlot key={key} title={title} subtitle={subtitle} />;
          }
          case enManagedComponents.banner_on_page: {
            const bannerData = isTruthy(data?.banners?.length) ? convertInBannerType(banners![0]) : undefined;
            return bannerData ? (
              <BannerLandscape key={key} data={bannerData} removePaddingLeft removePaddingRight={isTopTemplate} />
            ) : (
              <React.Fragment key={key} />
            );
          }
          case enManagedComponents.in_evidenza_block: {
            return <SportInEvidenza key={key} title={title} className="inEvidenzaContainer" />;
          }
          case enManagedComponents.top_antepost_block: {
            return <SportAntepost key={key} title={title} isTopTemplate={isTopTemplate} />;
          }
          case enManagedComponents.le_piu_giocate_block: {
            return <SportPiuGiocate key={key} title={title} isNotTopTemplate={!isTopTemplate} />;
          }
          case enManagedComponents.top_manifestazioni_block: {
            return <SportTopManifestazioni key={key} title={title} className="topManifestazioneContainer" />;
          }
          default: {
            return <React.Fragment key={key} />;
          }
        }
      });
  }, [pageId, cmsData, isTopTemplate]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <React.Fragment>{components}</React.Fragment>;
};

export { SportComposer };
